/**
 * SidebarQuickSearch — global quick search at the top of the sidebar.
 * Finds leads (by name/sector) and the user's custom menus/items without
 * opening folders. Backed by GET /api/quick-search.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, Loader2, User, Folder, Link as LinkIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SidebarQuickSearch({ navigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setResults(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onChange = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/quick-search`, { params: { q: val.trim() } });
        setResults(data);
      } catch (_e) { setResults(null); }
      finally { setLoading(false); }
    }, 350);
  };

  const go = (route) => {
    setQ(""); setResults(null);
    navigate(route);
  };

  const badgeDot = (b) =>
    b === "QUENTE" ? "#EF4444" : b === "FRIO" ? "#22C55E" : "#F97316";

  return (
    <div className="relative mb-2" ref={boxRef} data-testid="sidebar-quick-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
        <input
          value={q}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Buscar lead, nicho, pasta..."
          data-testid="quick-search-input"
          className="w-full h-9 pl-8 pr-8 rounded-lg bg-white/[0.06] border border-white/10 text-[12px] text-white placeholder-white/35 outline-none focus:border-[#C9A24E] focus:bg-white/[0.09] transition-colors"
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-white/40" />}
      </div>
      {results && (
        <div className="absolute left-0 right-0 top-10 z-50 rounded-lg bg-[#232342] border border-white/15 shadow-2xl py-1 max-h-[340px] overflow-y-auto"
          data-testid="quick-search-results">
          {(results.leads || []).length === 0 && (results.menus || []).length === 0 && (results.items || []).length === 0 && (
            <p className="px-3 py-2 text-[11px] text-white/40">Nada encontrado</p>
          )}
          {(results.leads || []).length > 0 && (
            <p className="px-3 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-wider text-white/35">Leads</p>
          )}
          {(results.leads || []).map((l) => (
            <button key={l.id} type="button" onClick={() => go(`/kanban?q=${encodeURIComponent(l.name)}`)}
              data-testid={`qs-lead-${l.id}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10">
              <User className="w-3 h-3 text-white/40 shrink-0" />
              <span className="flex-1 truncate text-[11.5px] text-white/85">{l.name}</span>
              <span className="text-[9.5px] text-white/40 truncate max-w-[70px]">{l.sector || ""}</span>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: badgeDot(l.badge) }} />
            </button>
          ))}
          {(results.menus || []).length > 0 && (
            <p className="px-3 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-wider text-white/35">Pastas</p>
          )}
          {(results.menus || []).map((m) => (
            <button key={m.id} type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("custom-menus-open", { detail: m.id }));
                setQ(""); setResults(null);
              }}
              data-testid={`qs-menu-${m.id}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10">
              <Folder className="w-3 h-3 text-[#C9A24E] shrink-0" />
              <span className="flex-1 truncate text-[11.5px] text-white/85">{m.name}</span>
            </button>
          ))}
          {(results.items || []).length > 0 && (
            <p className="px-3 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-wider text-white/35">Itens salvos</p>
          )}
          {(results.items || []).map((it, i) => (
            <button key={`${it.route}-${i}`} type="button" onClick={() => go(it.route)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10">
              <LinkIcon className="w-3 h-3 text-white/40 shrink-0" />
              <span className="flex-1 truncate text-[11.5px] text-white/85">{it.label}</span>
              <span className="text-[9.5px] text-white/40 truncate max-w-[70px]">{it.menu}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
